import type { Graphics } from 'pixi.js';
import { type RngState, nextFloat } from '../../shared/prng';
import { OVERFLOW } from '../ground-layout';
import { type Way, at } from '../../data/full/path-layout';
import { PATH_COLORS } from './path-way';

const CROWN = { min: 70, spread: 40, gap: 96, gapSpread: 44, out: 20, outSpread: 40 };

// Crowns along the long edges only: the camera never looks past them.
export function drawTreeLine(g: Graphics, way: Way, side: -1 | 1, rng: RngState, inset = 0): void {
  const crowns: { x: number; y: number; r: number; tone: number }[] = [];
  for (let along = -OVERFLOW; along < way.length + OVERFLOW;) {
    const r = CROWN.min + nextFloat(rng) * CROWN.spread;
    const out = CROWN.out + nextFloat(rng) * CROWN.outSpread - inset;
    const across = side < 0 ? -out : way.width + out;
    const { x, y } = at(way, along, across);
    crowns.push({ x, y, r, tone: crowns.length % 3 });
    along += CROWN.gap + nextFloat(rng) * CROWN.gapSpread;
  }
  for (const { x, y, r } of crowns) {
    g.circle(x + 14, y + 18, r).fill({ color: PATH_COLORS.shade, alpha: 0.55 });
  }
  const meadow = way.down ? { x: -side, y: 0 } : { x: 0, y: -side };
  const facing = Math.atan2(meadow.y, meadow.x);
  for (const { x, y, r, tone } of crowns) {
    drawCrown(g, x, y, r, tone, facing, rng);
  }
}

const TONES = [PATH_COLORS.canopyDark, PATH_COLORS.canopy, PATH_COLORS.canopyLight];

export function drawCrown(
  g: Graphics,
  x: number,
  y: number,
  r: number,
  tone: number,
  facing: number,
  rng: RngState,
): void {
  g.circle(x, y, r)
    .fill(TONES[tone] ?? PATH_COLORS.canopy)
    .stroke({ width: 2.5, color: PATH_COLORS.canopyEdge });
  for (let lobe = 0; lobe < 5; lobe++) {
    const angle = nextFloat(rng) * Math.PI * 2;
    g.circle(
      x + Math.cos(angle) * r * 0.55,
      y + Math.sin(angle) * r * 0.55,
      r * (0.3 + nextFloat(rng) * 0.2),
    )
      .fill(TONES[(tone + lobe) % 3] ?? PATH_COLORS.canopy)
      .stroke({ width: 1.5, color: PATH_COLORS.canopyEdge, alpha: 0.6 });
  }
  g.circle(x - r * 0.3, y - r * 0.3, r * 0.35).fill({
    color: PATH_COLORS.canopyLight,
    alpha: 0.45,
  });
  g.moveTo(x + Math.cos(facing - 0.9) * r, y + Math.sin(facing - 0.9) * r)
    .arc(x, y, r, facing - 0.9, facing + 0.9)
    .stroke({
      width: 3,
      color: PATH_COLORS.rim,
      alpha: 0.35,
    });
}
